import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";
import { canUpload, duplicateMediaFromError, findDuplicate, hasSameNameAndMime, mergeMediaAssets, type MediaLibraryStatus } from "./media-library-state";
import type { MediaAsset } from "./types";

function formatDuration(seconds?: number) {
  if (!seconds) return "";
  return Math.floor(seconds / 60) + ":" + Math.round(seconds % 60).toString().padStart(2, "0");
}
function assetUrl(asset: MediaAsset) { return "/api/media/" + asset.id + "/file"; }

export function MediaLibraryPage() {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [status, setStatus] = useState<MediaLibraryStatus>("loading");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [duplicate, setDuplicate] = useState<MediaAsset | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadInFlight = useRef(false);
  const requestId = useRef(0);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setStatus("loading"); setError("");
    try {
      const items = await api.media();
      if (!mounted.current || id !== requestId.current) return;
      setAssets(items); setStatus("ready");
    } catch (reason) {
      if (!mounted.current || id !== requestId.current) return;
      setError(reason instanceof Error ? reason.message : "Не удалось загрузить медиатеку"); setStatus("error");
    }
  }, []);
  useEffect(() => { mounted.current = true; void load(); return () => { mounted.current = false; requestId.current += 1; }; }, [load]);

  async function upload(file?: File) {
    if (!file || !canUpload(status) || uploadInFlight.current) return;
    uploadInFlight.current = true;
    setStatus("uploading"); setError(""); setWarning(""); setDuplicate(null);
    try {
      const known = await findDuplicate(file, assets);
      if (known) { if (mounted.current) setDuplicate(known); return; }
      if (assets.some((asset) => hasSameNameAndMime(file, asset)) && mounted.current) {
        setWarning("Файл с таким именем и типом уже есть, но его содержимое отличается. Существующий файл не будет перезаписан.");
      }
      const uploaded = await api.uploadMedia(file);
      if (mounted.current) setAssets((current) => mergeMediaAssets(current, [uploaded]));
    } catch (reason) {
      const existing = duplicateMediaFromError(reason);
      if (existing && mounted.current) {
        setDuplicate(existing); setAssets((current) => mergeMediaAssets(current, [existing]));
      } else if (mounted.current) setError(reason instanceof Error ? reason.message : "Не удалось загрузить медиафайл");
    } finally {
      uploadInFlight.current = false;
      if (mounted.current) setStatus("ready");
      if (inputRef.current) inputRef.current.value = "";
    }
  }
  async function remove(asset: MediaAsset) {
    if (!window.confirm("Удалить «" + asset.originalName + "»?")) return;
    try { await api.deleteMedia(asset.id); if (mounted.current) setAssets((current) => current.filter((item) => item.id !== asset.id)); }
    catch (reason) { if (mounted.current) setError(reason instanceof Error ? reason.message : "Не удалось удалить медиафайл"); }
  }
  const uploadEnabled = canUpload(status);
  return <section className="articles-section">
    <div className="section-heading media-library-heading"><div><h2>Медиатека</h2><p>Изображения, GIF и видео для статей. Привязанные файлы удаляются только вместе со статьёй.</p></div><button className="primary-button compact" type="button" disabled={!uploadEnabled} onClick={() => inputRef.current?.click()}>{status === "uploading" ? "Загрузка…" : "+ Загрузить"}</button></div>
    <input ref={inputRef} className="visually-hidden" disabled={!uploadEnabled} type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm" onChange={(event) => void upload(event.target.files?.[0])} />
    {status === "loading" ? <div className="loading-state">Загружаем медиатеку…</div> : null}
    {status === "error" ? <div className="notice error" role="alert">{error}<button type="button" className="quiet-button" onClick={() => void load()}>Повторить загрузку медиатеки</button></div> : null}
    {error && status !== "error" ? <div className="notice error" role="alert">{error}</div> : null}
    {warning ? <div className="notice warning" role="status">{warning}</div> : null}
    {duplicate ? <div className="notice warning" role="status"><b>Такой файл уже есть в медиатеке</b><span>{duplicate.originalName} · {duplicate.mimeType} · {(duplicate.byteSize / 1_048_576).toFixed(2)} МБ</span></div> : null}
    {status !== "loading" && status !== "error" ? assets.length ? <div className="library-grid">{assets.map((asset) => <article className="library-card" key={asset.id}>
      {asset.kind === "video" ? <video src={assetUrl(asset)} controls preload="metadata" /> : <img src={assetUrl(asset)} alt="" loading="lazy" />}
      <div><b>{asset.originalName}</b><span>{asset.kind === "video" ? "Видео" : "Изображение"} · {asset.width} × {asset.height}{formatDuration(asset.durationSeconds) ? " · " + formatDuration(asset.durationSeconds) : ""} · {(asset.byteSize / 1_048_576).toFixed(2)} МБ</span><small>{asset.articleId ? "Используется в статье" : "Не привязано"}</small>{!asset.articleId ? <button type="button" className="danger-link" onClick={() => void remove(asset)}>Удалить</button> : null}</div>
    </article>)}</div> : <div className="empty-state"><span className="empty-icon">▧</span><h3>Медиатеки пока нет</h3><p>Загрузите первый файл для использования в статье.</p></div> : null}
  </section>;
}
